import { useState, useEffect, useCallback, useMemo } from "react";
import api from "../utils/api";
import { windColor, sensorLage } from "../utils/wind";

const PRESETS = [
  { label: "6 Std.",  hours: 6 },
  { label: "12 Std.", hours: 12 },
  { label: "24 Std.", hours: 24 },
  { label: "3 Tg.",   hours: 72 },
  { label: "7 Tg.",   hours: 168 },
  { label: "30 Tg.",  hours: 720 },
  { label: "90 Tg.",  hours: 2160 },
];

const SENSOR_COLORS = [
  "#22c55e","#3b82f6","#f97316","#a855f7","#eab308","#ef4444","#06b6d4","#f43f5e","#84cc16","#0ea5e9",
  "#f43f5e","#84cc16","#0ea5e9"
];

export default function WindCharts({ sensors }) {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [hours, setHours] = useState(24);
  const [activePreset, setActivePreset] = useState("24 Std.");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [customMode, setCustomMode] = useState(false);
  const [visibleWind, setVisibleWind] = useState({});
  const [visibleGust, setVisibleGust] = useState({});
  const [earliest, setEarliest] = useState(null);
  const [expandedWind, setExpandedWind] = useState(false);
  const [expandedGust, setExpandedGust] = useState(false);

  // Frühestes Datum einmalig laden
  useEffect(() => {
    api.get("/measurements/data-range").then(r => {
      if (r.data.earliest) setEarliest(r.data.earliest.substring(0,16));
    }).catch(() => {});
  }, []);

  // Sichtbarkeit nur (re-)initialisieren, wenn sich die Sensor-IDs ändern.
  // Beim reinen Datenrefresh bleibt die Auswahl des Benutzers erhalten.
  const sensorKey = sensors.map(s => s.id).sort((a,b) => a-b).join(",");
  useEffect(() => {
    const merge = prev => {
      const v = {};
      sensors.forEach(s => { v[s.id] = prev[s.id] === undefined ? true : prev[s.id]; });
      return v;
    };
    setVisibleWind(merge);
    setVisibleGust(merge);
  }, [sensorKey]);

  const setAll = (val) => {
    const v = {};
    sensors.forEach(s => { v[s.id] = val; });
    setVisibleWind(v);
    setVisibleGust(v);
  };

  const loadData = useCallback(async () => {
    if (sensors.length === 0) return;
    setLoading(true);
    try {
      let from, to;
      if (customMode && dateFrom && dateTo) {
        from = new Date(dateFrom).toISOString();
        to = new Date(dateTo).toISOString();
      } else {
        to = new Date().toISOString();
        from = new Date(Date.now() - hours * 3600000).toISOString();
      }
      const r = await api.get(`/measurements/compare?date_from=${from}&date_to=${to}`);
      setData(r.data);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [sensors, hours, customMode, dateFrom, dateTo]);

  useEffect(() => { loadData(); }, [loadData]);

  // Berechne SVG-Linien fuer jeden Sensor
  const charts = useMemo(() => {
    if (!data || sensors.length === 0) return { wind: [], gust: [] };
    const allPoints = [];
    sensors.forEach(s => {
      const pts = data[s.name] || [];
      pts.forEach(p => allPoints.push(new Date(p.t).getTime()));
    });
    if (allPoints.length === 0) return { wind: [], gust: [] };
    const tMin = Math.min(...allPoints);
    const tMax = Math.max(...allPoints);
    const tRange = tMax - tMin || 1;

    const W = 1000, H = 160, PAD = { l:40, r:10, t:10, b:30 };
    const gW = W - PAD.l - PAD.r;
    const gH = H - PAD.t - PAD.b;

    const makeLines = (key) => sensors.map((s, si) => {
      const pts = (data[s.name] || []).filter(p => p[key] !== null);
      if (pts.length < 2) return null;
      const allVals = sensors.flatMap(sx => (data[sx.name]||[]).map(p => p[key]||0));
      const vMax = Math.max(...allVals, 0.1);
      const toX = t => PAD.l + ((new Date(t).getTime() - tMin) / tRange) * gW;
      const toY = v => PAD.t + gH - (v / vMax) * gH;
      const d = pts.map((p,i) => `${i===0?"M":"L"}${toX(p.t).toFixed(1)},${toY(p[key]).toFixed(1)}`).join(" ");
      return { id: s.id, name: s.name, label: sensorLage(s) ? `${s.name} (${sensorLage(s)})` : s.name, color: SENSOR_COLORS[si % SENSOR_COLORS.length], path: d, vMax, tMin, tMax, W, H, PAD, gW, gH };
    }).filter(Boolean);

    return { wind: makeLines("ws"), gust: makeLines("wg") };
  }, [data, sensors]);

  const toggleSensor = (id, chart) => {
    if (chart === "wind") setVisibleWind(v => ({ ...v, [id]: !v[id] }));
    else setVisibleGust(v => ({ ...v, [id]: !v[id] }));
  };

  const yTicks = (vMax) => {
    const step = vMax <= 5 ? 1 : vMax <= 15 ? 3 : 5;
    const ticks = [];
    for (let v = 0; v <= vMax; v += step) ticks.push(v);
    return ticks;
  };

  const xTicks = (tMin, tMax, gW, PAD) => {
    const range = tMax - tMin;
    const count = 6;
    return Array.from({length: count+1}, (_,i) => {
      const t = tMin + (range / count) * i;
      const x = PAD.l + (i / count) * gW;
      const d = new Date(t);
      const label = range < 86400000*2
        ? d.toLocaleTimeString("de-CH", {hour:"2-digit", minute:"2-digit"})
        : d.toLocaleDateString("de-CH", {day:"2-digit", month:"2-digit"});
      return { x, label };
    });
  };

  const renderChart = (lines, visible, setVisible, title, key, expanded, onToggle) => {
    const firstLine = lines[0];
    if (!firstLine) return (
      <div style={S.noData}>Keine Daten</div>
    );
    const { vMax, tMin, tMax, W, H, PAD, gW, gH } = firstLine;
    const yt = yTicks(vMax);
    const xt = xTicks(tMin, tMax, gW, PAD);

    return (
      <div style={S.chartBox}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <div style={S.chartTitle}>{title}</div>
          <button onClick={onToggle} style={S.expandBtn} title={expanded ? "Verkleinern" : "Vergroessern"}>{expanded ? "⊠ Verkleinern" : "⊞ Vergroessern"}</button>
        </div>
        <div style={S.legend}>
          {lines.map(l => (
            <button key={l.id} onClick={() => toggleSensor(l.id, key)}
              style={{ ...S.legendBtn, opacity: visible[l.id] ? 1 : 0.35, borderColor: l.color }}>
              <span style={{ ...S.legendDot, background: l.color }} />
              {l.label}
            </button>
          ))}
          <button onClick={() => setAll(true)} style={S.legendCtl}>Alle ein</button>
          <button onClick={() => setAll(false)} style={S.legendCtl}>Alle aus</button>
        </div>
        <svg viewBox={`0 0 ${W} ${H}`} style={{ width:"100%", height: expanded ? 320 : 180, display:"block", transition:"height 0.25s ease" }}>
          {/* Grid */}
          {yt.map(v => {
            const y = PAD.t + gH - (v / vMax) * gH;
            return <g key={v}>
              <line x1={PAD.l} y1={y} x2={PAD.l+gW} y2={y} stroke="#1e3a5f" strokeWidth="1" strokeDasharray="4,3" />
              <text x={PAD.l-4} y={y+4} textAnchor="end" fill="#4a6a8a" fontSize="11" fontFamily="sans-serif">{v}</text>
            </g>;
          })}
          {/* X ticks */}
          {xt.map((t,i) => (
            <g key={i}>
              <line x1={t.x} y1={PAD.t+gH} x2={t.x} y2={PAD.t+gH+4} stroke="#1e3a5f" strokeWidth="1" />
              <text x={t.x} y={H-4} textAnchor="middle" fill="#4a6a8a" fontSize="10" fontFamily="sans-serif">{t.label}</text>
            </g>
          ))}
          {/* Axes */}
          <line x1={PAD.l} y1={PAD.t} x2={PAD.l} y2={PAD.t+gH} stroke="#2a4a6a" strokeWidth="1.5" />
          <line x1={PAD.l} y1={PAD.t+gH} x2={PAD.l+gW} y2={PAD.t+gH} stroke="#2a4a6a" strokeWidth="1.5" />
          {/* Y label */}
          <text x={10} y={PAD.t+gH/2} textAnchor="middle" fill="#4a6a8a" fontSize="11" fontFamily="sans-serif"
            transform={`rotate(-90, 10, ${PAD.t+gH/2})`}>m/s</text>
          {/* Lines */}
          {lines.map(l => visible[l.id] && (
            <path key={l.id} d={l.path} fill="none" stroke={l.color} strokeWidth="1.5"
              strokeLinejoin="round" strokeLinecap="round" opacity="0.9" />
          ))}
        </svg>
      </div>
    );
  };

  return (
    <div style={S.wrapper}>
      <div style={S.header}>
        <span style={S.title}>Windauswertung</span>
        <div style={S.controls}>
          {PRESETS.map(p => (
            <button key={p.label} onClick={() => { setHours(p.hours); setActivePreset(p.label); setCustomMode(false); }}
              style={{ ...S.presetBtn, background: activePreset===p.label && !customMode ? "#1d4ed8":"#1e3a5f" }}>
              {p.label}
            </button>
          ))}
          <button onClick={() => setCustomMode(!customMode)}
            style={{ ...S.presetBtn, background: customMode ? "#1d4ed8":"#1e3a5f" }}>
            Zeitraum
          </button>
          {loading && <span style={S.loading}>laden...</span>}
        </div>
      </div>
      {customMode && (
        <div style={S.dateRow}>
          <label style={S.dateLabel}>Von</label>
          <input type="datetime-local" value={dateFrom} min={earliest||""} onChange={e=>setDateFrom(e.target.value)} style={S.dateInput} />
          <label style={S.dateLabel}>Bis</label>
          <input type="datetime-local" value={dateTo} onChange={e=>setDateTo(e.target.value)} style={S.dateInput} />
          <button onClick={loadData} style={S.goBtn}>Anzeigen</button>
          {earliest && <span style={S.hint}>Daten ab {earliest.replace("T"," ")}</span>}
        </div>
      )}
      {renderChart(charts.wind, visibleWind, setVisibleWind, "Windgeschwindigkeit (m/s)", "wind", expandedWind, () => setExpandedWind(v => !v))}
      {renderChart(charts.gust, visibleGust, setVisibleGust, "Windboeen (m/s)", "gust", expandedGust, () => setExpandedGust(v => !v))}
    </div>
  );
}

const S = {
  wrapper: { display:"flex", flexDirection:"column", gap:16, background:"#080f1e", borderRadius:12, padding:"16px 20px", border:"1px solid #1e3a5f", marginTop:16 },
  header: { display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:8 },
  title: { color:"#e2e8f0", fontWeight:700, fontSize:15 },
  controls: { display:"flex", gap:6, flexWrap:"wrap", alignItems:"center" },
  presetBtn: { color:"#e2e8f0", border:"none", borderRadius:5, padding:"5px 12px", fontSize:12, cursor:"pointer", fontWeight:600 },
  loading: { color:"#4a6a8a", fontSize:12 },
  dateRow: { display:"flex", alignItems:"center", gap:10, flexWrap:"wrap", background:"#0f1e35", borderRadius:8, padding:"10px 14px" },
  dateLabel: { color:"#94a3b8", fontSize:12 },
  dateInput: { background:"#1e3a5f", border:"1px solid #2a4a6a", borderRadius:5, color:"#e2e8f0", padding:"4px 8px", fontSize:12 },
  goBtn: { background:"#1d4ed8", color:"#fff", border:"none", borderRadius:5, padding:"5px 14px", fontSize:12, cursor:"pointer", fontWeight:600 },
  hint: { color:"#4a6a8a", fontSize:11 },
  chartBox: { display:"flex", flexDirection:"column", gap:8 },
  chartTitle: { color:"#94a3b8", fontSize:13, fontWeight:600 },
  legend: { display:"flex", gap:8, flexWrap:"wrap" },
  legendBtn: { background:"transparent", border:"1px solid", borderRadius:5, padding:"3px 10px", fontSize:11, cursor:"pointer", color:"#c0d0e8", display:"flex", alignItems:"center", gap:5, fontFamily:"sans-serif" },
  legendDot: { width:8, height:8, borderRadius:"50%", flexShrink:0 },
  legendCtl: { background:"#1e3a5f", border:"1px solid #2a4a6a", borderRadius:5, padding:"3px 10px", fontSize:11, cursor:"pointer", color:"#94a3b8", fontWeight:600, fontFamily:"sans-serif" },
  noData: { color:"#334466", fontSize:13, textAlign:"center", padding:"20px 0" },
  expandBtn: { background:"#1e3a5f", border:"1px solid #2a4a6a", color:"#94a3b8", borderRadius:5, padding:"3px 10px", fontSize:11, cursor:"pointer", fontWeight:600, fontFamily:"sans-serif" },
};
